<script setup lang="ts">
import { computed, type Ref, type WritableComputedRef } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import BaseTextInput from '@/components/ui/BaseTextInput.vue';
import BaseDropdown from '@/components/ui/BaseDropdown.vue';
import BaseDateInput from '@/components/ui/BaseDateInput.vue';
import type { DropdownOption } from '@/components/ui/BaseDropdown.vue';
import { NATIONALITY_OPTIONS } from '@/constants/checkout/nationalities';
// COUNTRY_OPTIONS only used by the country dropdown, hidden for additional travelers.
// import { COUNTRY_OPTIONS } from '@/constants/checkout/countries';
import { $t } from '@/composables/translations/useTranslate';
import {
  type AdultTravelerFormValues,
  type ChildTravelerFormValues,
} from '@/types/schemas/checkout/traveler';
import { createTravelerFormSchema } from '@/types/schemas/checkout/travelerFormSchema';
import type { TravelerVariant } from '@/types/schemas/checkout/travelerVariant';
import {
  ageRangeForVariant,
  isEmptyRange,
  deriveBirthDateBounds,
  type AgeBands,
} from '@/types/schemas/checkout/ageBands';
import { ADULT_MIN_AGE } from '@/types/schemas/checkout/validation';

/**
 * Checkout Traveler Form Component
 *
 * Renders individual traveler forms with dynamic field sets based on the 4-way
 * traveler variant. Owns its own useForm() instance and exposes validation methods
 * via defineExpose().
 *
 * - Adult variant: salutation, birthDate, firstName, lastName, nationality (age >= 18 at
 *   check-in). Address + contact fields (street, postalCode, location, country, mobile,
 *   email) are temporarily hidden — see the schema comment in
 *   types/schemas/checkout/traveler.ts for the restore path.
 * - Gender-style variants (under18adult / child / infant): gender, firstName, lastName,
 *   birthDate, nationality. The valid age range is variant-specific and comes from the
 *   hotel's age bands; it is evaluated at check-in.
 *
 * Validation error messages from Zod schemas are translation keys
 * that are passed directly to $t() for translation.
 *
 * IMPORTANT: The form schema is determined at component mount from travelerVariant +
 * checkInDate + ageBands. These props MUST NOT change after mount — if they do, the
 * component keeps using the original schema. This is by design since passenger variants
 * derive from URL parameters and the bands are frozen once the probe resolves.
 */

interface Props {
  travelerId: number;
  travelerVariant: TravelerVariant;
  travelerNumber: number;
  ageBands: AgeBands;
  checkInDate: Date;
  /**
   * Whether this passenger carries a hotel `hotelClass`. Defaults to `true`; pass `false`
   * for binary/plain URLs so the `child` variant accepts 0–2 year olds (see ageBands).
   */
  isClassified?: boolean;
  initialValues?: Partial<AdultTravelerFormValues | ChildTravelerFormValues>;
}

const props = defineProps<Props>();

// In binary/plain mode (no hotelClass) the `child` variant means "any non-adult"; treat
// an unset prop as classified to preserve the probe-driven ranges for the normal flow.
const isClassified = props.isClassified !== false;

function getDefaultAdultValues(): Partial<AdultTravelerFormValues> {
  return {
    salutation: undefined,
    birthDate: '',
    firstName: '',
    lastName: '',
    // Address & contact fields hidden for additional travelers.
    // street: '',
    // postalCode: '',
    // location: '',
    // country: undefined,
    nationality: undefined,
    // mobile: '',
    // email: '',
  };
}

function getDefaultChildValues(): Partial<ChildTravelerFormValues> {
  return {
    gender: undefined,
    firstName: '',
    lastName: '',
    birthDate: '',
    nationality: undefined,
  };
}

// Form Setup
// The adult variant is salutation-style; under18adult / child / infant are gender-style.
// The branch is frozen at mount (the variant never changes for a passenger).
const isAdultVariant = props.travelerVariant === 'adult';

// Schema is built ONCE from the frozen variant + check-in date + age bands.
const formSchema = createTravelerFormSchema(
  props.travelerVariant,
  props.checkInDate,
  props.ageBands,
  isClassified
);

// We need to use type assertions here because TypeScript can't narrow at compile time
/* eslint-disable @typescript-eslint/no-explicit-any */
const formInstance = useForm<any>({
  validationSchema: toTypedSchema(formSchema),
  initialValues:
    props.initialValues ?? (isAdultVariant ? getDefaultAdultValues() : getDefaultChildValues()),
});

type FieldRef = Ref<string | undefined>;

const [rawFirstNameValue] = formInstance.defineField('firstName') as [FieldRef, unknown];
const [rawLastNameValue] = formInstance.defineField('lastName') as [FieldRef, unknown];
const [rawBirthDateValue] = formInstance.defineField('birthDate') as [FieldRef, unknown];
const [rawNationalityValue] = formInstance.defineField('nationality') as [FieldRef, unknown];

// Variant-specific field: salutation (adult) vs gender (under18adult / child / infant).
let rawSalutationValue: FieldRef | null = null;
let rawGenderValue: FieldRef | null = null;
// Address & contact fields hidden for additional travelers.
// let rawStreetValue: FieldRef | null = null;
// let rawPostalCodeValue: FieldRef | null = null;
// let rawLocationValue: FieldRef | null = null;
// let rawCountryValue: FieldRef | null = null;
// let rawMobileValue: FieldRef | null = null;
// let rawEmailValue: FieldRef | null = null;

if (isAdultVariant) {
  [rawSalutationValue] = formInstance.defineField('salutation') as [FieldRef, unknown];
  // Address & contact fields hidden for additional travelers.
  // [rawStreetValue] = formInstance.defineField('street') as [FieldRef, unknown];
  // [rawPostalCodeValue] = formInstance.defineField('postalCode') as [FieldRef, unknown];
  // [rawLocationValue] = formInstance.defineField('location') as [FieldRef, unknown];
  // [rawCountryValue] = formInstance.defineField('country') as [FieldRef, unknown];
  // [rawMobileValue] = formInstance.defineField('mobile') as [FieldRef, unknown];
  // [rawEmailValue] = formInstance.defineField('email') as [FieldRef, unknown];
} else {
  [rawGenderValue] = formInstance.defineField('gender') as [FieldRef, unknown];
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// Computed Wrappers for v-modell
const firstNameValue = computed({
  get: () => rawFirstNameValue.value,
  set: (val) => {
    rawFirstNameValue.value = val;
  },
});

const lastNameValue = computed({
  get: () => rawLastNameValue.value,
  set: (val) => {
    rawLastNameValue.value = val;
  },
});

const birthDateValue = computed({
  get: () => rawBirthDateValue.value,
  set: (val) => {
    rawBirthDateValue.value = val;
  },
});

const salutationValue: WritableComputedRef<string | undefined> = computed({
  get: () => rawSalutationValue?.value,
  set: (val) => {
    if (rawSalutationValue) rawSalutationValue.value = val;
  },
});

const genderValue: WritableComputedRef<string | undefined> = computed({
  get: () => rawGenderValue?.value,
  set: (val) => {
    if (rawGenderValue) rawGenderValue.value = val;
  },
});

// Address & contact fields hidden for additional travelers.
// const streetValue: WritableComputedRef<string | undefined> = computed({
//   get: () => rawStreetValue?.value,
//   set: (val) => {
//     if (rawStreetValue) rawStreetValue.value = val;
//   },
// });

// const postalCodeValue: WritableComputedRef<string | undefined> = computed({
//   get: () => rawPostalCodeValue?.value,
//   set: (val) => {
//     if (rawPostalCodeValue) rawPostalCodeValue.value = val;
//   },
// });

// const locationValue: WritableComputedRef<string | undefined> = computed({
//   get: () => rawLocationValue?.value,
//   set: (val) => {
//     if (rawLocationValue) rawLocationValue.value = val;
//   },
// });

// const countryValue: WritableComputedRef<string | undefined> = computed({
//   get: () => rawCountryValue?.value,
//   set: (val) => {
//     if (rawCountryValue) rawCountryValue.value = val;
//   },
// });

// const mobileValue: WritableComputedRef<string | undefined> = computed({
//   get: () => rawMobileValue?.value,
//   set: (val) => {
//     if (rawMobileValue) rawMobileValue.value = val;
//   },
// });

// const emailValue: WritableComputedRef<string | undefined> = computed({
//   get: () => rawEmailValue?.value,
//   set: (val) => {
//     if (rawEmailValue) rawEmailValue.value = val;
//   },
// });

const nationalityValue: WritableComputedRef<string | undefined> = computed({
  get: () => rawNationalityValue.value,
  set: (val) => {
    rawNationalityValue.value = val;
  },
});

const errors = computed(() => formInstance.errors.value);

/**
 * Translated errors computed property.
 * Error messages from Zod schemas are translation keys (e.g., 'ibe.validation.firstName.required')
 * that are passed directly to $t() for translation.
 */
const translatedErrors = computed(() => {
  const result: Record<string, string> = {};
  for (const [field, message] of Object.entries(errors.value)) {
    result[field] = message ? $t(message) : '';
  }
  return result;
});

// Exposed Methods (for parent component via ref)
defineExpose({
  async validate() {
    const result = await formInstance.validate();
    return {
      valid: result.valid,
      errors: { ...formInstance.errors.value } as Record<string, string>,
    };
  },
  getValues(): AdultTravelerFormValues | ChildTravelerFormValues {
    return { ...formInstance.values } as AdultTravelerFormValues | ChildTravelerFormValues;
  },
  getErrors(): Record<string, string | undefined> {
    return { ...formInstance.errors.value };
  },
  reset() {
    formInstance.resetForm();
  },
  isDirty(): boolean {
    return formInstance.meta.value.dirty;
  },
});

const FORM_TITLE: Record<TravelerVariant, { key: string; fallback: string }> = {
  adult: { key: 'ibe.checkout.traveler.adult-title', fallback: 'Traveler {number}' },
  under18adult: { key: 'ibe.checkout.traveler.youth-title', fallback: 'Youth {number}' },
  child: { key: 'ibe.checkout.traveler.child-title', fallback: 'Child {number}' },
  infant: { key: 'ibe.checkout.traveler.infant-title', fallback: 'Infant {number}' },
};

const formTitle = computed(() => {
  const { key, fallback } = FORM_TITLE[props.travelerVariant];
  return $t(key, fallback, { number: props.travelerNumber });
});

/**
 * Permanent age hint shown under the birth-date input (always visible, not a Zod error).
 * Tells the user the valid age window for this variant at check-in.
 *
 * - adult: floored at a fixed 18 (ADULT_MIN_AGE), NOT the band's `adultMin` (§ Phase 6).
 * - under18adult with an empty range (band unknown): the loose "< 18" fallback copy.
 */
const ageHintText = computed(() => {
  const variant = props.travelerVariant;
  const range = ageRangeForVariant(variant, props.ageBands, isClassified);

  if (variant === 'under18adult' && isEmptyRange(range)) {
    return $t(
      'ibe.checkout.traveler.age-hint.under18adult-fallback',
      'Must be under 18 years old on the arrival date'
    );
  }

  const min = variant === 'adult' ? ADULT_MIN_AGE : range.minAge;
  const max = range.maxAge;
  const fallback: Record<TravelerVariant, string> = {
    adult: 'Must be at least {min} years old on the arrival date',
    under18adult: 'Must be between {min} and 17 years old on the arrival date',
    child: 'Must be between {min} and {max} years old on the arrival date',
    infant: 'Must be at most {max} years old on the arrival date',
  };
  return $t(`ibe.checkout.traveler.age-hint.${variant}`, fallback[variant], { min, max });
});

/**
 * Date-picker bounds. For the adult variant the floor is a fixed 18 (the band's `adultMin`
 * can sit below 18), so cap the picker at "18 at check-in"; the other variants derive from
 * the bands directly.
 */
const dateBounds = computed<{ minDate?: Date; maxDate?: Date }>(() => {
  if (props.travelerVariant === 'adult') {
    const checkIn = props.checkInDate;
    return {
      maxDate: new Date(
        checkIn.getFullYear() - ADULT_MIN_AGE,
        checkIn.getMonth(),
        checkIn.getDate()
      ),
    };
  }
  return deriveBirthDateBounds(
    props.travelerVariant,
    props.ageBands,
    props.checkInDate,
    isClassified
  );
});

const salutationOptions = computed<DropdownOption[]>(() => [
  { value: 'mr', label: $t('ibe.form.salutation.mr', 'Mr') },
  { value: 'mrs', label: $t('ibe.form.salutation.mrs', 'Mrs') },
  { value: 'diverse', label: $t('ibe.form.salutation.diverse', 'Diverse') },
]);

const genderOptions = computed<DropdownOption[]>(() => [
  { value: 'male', label: $t('ibe.form.gender.male', 'Male') },
  { value: 'female', label: $t('ibe.form.gender.female', 'Female') },
  { value: 'diverse', label: $t('ibe.form.gender.diverse', 'Diverse') },
]);
</script>

<template>
  <div class="checkout-traveler-form mb-12">
    <h3 class="h3-styling mb-6 text-text-primary">{{ formTitle }}</h3>

    <!-- Gender-style Form — under18adult / child / infant (5 fields) -->
    <template v-if="!isAdultVariant">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
        <BaseDropdown
          :id="`traveler-${travelerId}-gender`"
          v-model="genderValue"
          name="gender"
          :label="$t('ibe.form.label.gender', 'Gender *')"
          :placeholder="$t('ibe.form.placeholder.gender', 'Select gender')"
          :options="genderOptions"
          :has-error="!!errors.gender"
          :error-message="translatedErrors.gender"
        />

        <div>
          <BaseDateInput
            :id="`traveler-${travelerId}-birthDate`"
            v-model="birthDateValue"
            name="birthDate"
            :label="$t('ibe.form.label.birthDate', 'Birth date *')"
            :placeholder="$t('ibe.form.placeholder.birthDate', 'DD.MM.YYYY (e.g. 25.12.1980)')"
            :min-date="dateBounds.minDate"
            :max-date="dateBounds.maxDate"
            :error="!!errors.birthDate"
            :error-message="translatedErrors.birthDate"
          />
          <p class="mt-2 text-body-small text-text-primary/60">{{ ageHintText }}</p>
        </div>

        <BaseTextInput
          :id="`traveler-${travelerId}-firstName`"
          v-model="firstNameValue"
          type="text"
          name="firstName"
          :label="$t('ibe.form.label.firstName', 'First name *')"
          :placeholder="$t('ibe.form.placeholder.firstName', 'Full first name(s) as per passport')"
          :error="!!errors.firstName"
          :error-message="translatedErrors.firstName"
        />

        <BaseTextInput
          :id="`traveler-${travelerId}-lastName`"
          v-model="lastNameValue"
          type="text"
          name="lastName"
          :label="$t('ibe.form.label.lastName', 'Last name *')"
          :placeholder="$t('ibe.form.placeholder.lastName', 'Last name as per passport')"
          :error="!!errors.lastName"
          :error-message="translatedErrors.lastName"
        />

        <BaseDropdown
          :id="`traveler-${travelerId}-nationality`"
          v-model="nationalityValue"
          name="nationality"
          :label="$t('ibe.form.label.nationality', 'Nationality *')"
          :placeholder="$t('ibe.form.placeholder.nationality', 'Select nationality')"
          :options="NATIONALITY_OPTIONS"
          :has-error="!!errors.nationality"
          :error-message="translatedErrors.nationality"
        />
      </div>
    </template>

    <!-- Adult Traveler Form (10 fields) -->
    <template v-else>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
        <BaseDropdown
          :id="`traveler-${travelerId}-salutation`"
          v-model="salutationValue"
          name="salutation"
          :label="$t('ibe.form.label.salutation', 'Salutation *')"
          :placeholder="$t('ibe.form.placeholder.salutation', 'Select salutation')"
          :options="salutationOptions"
          :has-error="!!errors.salutation"
          :error-message="translatedErrors.salutation"
        />

        <div>
          <BaseDateInput
            :id="`traveler-${travelerId}-birthDate`"
            v-model="birthDateValue"
            name="birthDate"
            :label="$t('ibe.form.label.birthDate', 'Birth date *')"
            :placeholder="$t('ibe.form.placeholder.birthDate', 'DD.MM.YYYY (e.g. 25.12.1980)')"
            :min-date="dateBounds.minDate"
            :max-date="dateBounds.maxDate"
            :error="!!errors.birthDate"
            :error-message="translatedErrors.birthDate"
          />
          <p class="mt-2 text-body-small text-text-primary/60">{{ ageHintText }}</p>
        </div>

        <BaseTextInput
          :id="`traveler-${travelerId}-firstName`"
          v-model="firstNameValue"
          type="text"
          name="firstName"
          :label="$t('ibe.form.label.firstName', 'First name *')"
          :placeholder="$t('ibe.form.placeholder.firstName', 'Full first name(s) as per passport')"
          :error="!!errors.firstName"
          :error-message="translatedErrors.firstName"
        />

        <BaseTextInput
          :id="`traveler-${travelerId}-lastName`"
          v-model="lastNameValue"
          type="text"
          name="lastName"
          :label="$t('ibe.form.label.lastName', 'Last name *')"
          :placeholder="$t('ibe.form.placeholder.lastName', 'Last name as per passport')"
          :error="!!errors.lastName"
          :error-message="translatedErrors.lastName"
        />

        <!--
          Address & contact fields (street, postal code, city, country) hidden for
          additional travelers. To restore, uncomment this block plus
          the matching script plumbing and the required validators in
          types/schemas/checkout/traveler.ts.

        <BaseTextInput
          :id="`traveler-${travelerId}-street`"
          v-model="streetValue"
          type="text"
          name="street"
          :label="$t('ibe.form.label.street', 'Street and house number *')"
          :placeholder="$t('ibe.form.placeholder.street', 'e.g. Sample Street 12')"
          :error="!!errors.street"
          :error-message="translatedErrors.street"
        />

        <BaseTextInput
          :id="`traveler-${travelerId}-postalCode`"
          v-model="postalCodeValue"
          type="text"
          name="postalCode"
          :label="$t('ibe.form.label.postalCode', 'Postal code *')"
          :placeholder="$t('ibe.form.placeholder.postalCode', 'e.g. 10115')"
          :error="!!errors.postalCode"
          :error-message="translatedErrors.postalCode"
        />

        <BaseTextInput
          :id="`traveler-${travelerId}-location`"
          v-model="locationValue"
          type="text"
          name="location"
          :label="$t('ibe.form.label.location', 'City *')"
          :placeholder="$t('ibe.form.placeholder.location', 'e.g. Berlin')"
          :error="!!errors.location"
          :error-message="translatedErrors.location"
        />

        <BaseDropdown
          :id="`traveler-${travelerId}-country`"
          v-model="countryValue"
          name="country"
          :label="$t('ibe.form.label.country', 'Country *')"
          :placeholder="$t('ibe.form.placeholder.country', 'Select country')"
          :options="COUNTRY_OPTIONS"
          :has-error="!!errors.country"
          :error-message="translatedErrors.country"
        />
        -->

        <BaseDropdown
          :id="`traveler-${travelerId}-nationality`"
          v-model="nationalityValue"
          name="nationality"
          :label="$t('ibe.form.label.nationality', 'Nationality *')"
          :placeholder="$t('ibe.form.placeholder.nationality', 'Select nationality')"
          :options="NATIONALITY_OPTIONS"
          :has-error="!!errors.nationality"
          :error-message="translatedErrors.nationality"
        />

        <!--
          Contact fields (mobile, email) hidden for additional travelers.
          To restore, uncomment this block plus the matching script plumbing and the
          required validators in types/schemas/checkout/traveler.ts.

        <BaseTextInput
          :id="`traveler-${travelerId}-mobile`"
          v-model="mobileValue"
          type="tel"
          name="mobile"
          :label="$t('ibe.form.label.mobile', 'Mobile phone *')"
          :placeholder="$t('ibe.form.placeholder.mobile', 'e.g. +49 160 1234567')"
          :error="!!errors.mobile"
          :error-message="translatedErrors.mobile"
        />

        <BaseTextInput
          :id="`traveler-${travelerId}-email`"
          v-model="emailValue"
          type="email"
          name="email"
          :label="$t('ibe.form.label.email', 'Email address *')"
          :placeholder="$t('ibe.form.placeholder.email', 'e.g. name@email.de')"
          :error="!!errors.email"
          :error-message="translatedErrors.email"
        />
        -->
      </div>
    </template>
  </div>
</template>
